/**
 * Analytics tab — the LifeScribe original's layout, with real numbers only.
 *
 * Structure is the original's, element for element: four KPI Cards in a
 * `Grid` (xs 12 / sm 6 / md 3) — Writing Styles, Illustration Styles, a usage
 * tile and Avg Rating — each a caption, an `h4` value and a 40px icon at 0.7
 * opacity in primary / secondary / success / warning; then two half-width cards
 * holding two 300px-high panes.
 *
 * Where the original fabricates, this does not:
 *
 *  • The original's "Usage Over Time" series is `Math.random()`
 *    (`../lifescribe` useStylesData.ts:164-171), regenerated on every render, so
 *    the "trend" changes if you blink. Quill's usage endpoint (migration 570 /
 *    quill-admin-backend `styleUsage.js`) returns TOTALS PER STYLE and no time
 *    buckets, so there is no series to plot and this card does not pretend
 *    there is: in that slot, at the same size, it ranks the styles that were
 *    actually used, with the date the counting starts from beside them.
 *  • The usage tile is now a real count rather than an em dash — but only in
 *    the one state where a count is a true statement. Loading, a failed
 *    request, "570 is not applied here" and "the table is empty" each draw
 *    their own message and NONE of them draws a zero: a style nobody has used
 *    and a style used before recording existed are different facts.
 *  • "Avg Rating" is computed by the original from a counter it always reads as
 *    0, so it prints a confident `0.0`. Quill collects no ratings ANYWHERE —
 *    570 records use and holds no score — so this tile keeps its em dash and
 *    will keep it until something actually collects one. Deriving a rating from
 *    usage would be fabrication with an extra step.
 *  • "Styles by Category" IS real: the four catalogue totals. (The original
 *    feeds this chart rows keyed `category` while labelling by `name`, so its
 *    slice labels read "undefined 25%". Ours are keyed `name` and render.)
 *
 * TWO SOURCES, NEVER MIXED. Catalogue figures are counted from the rows the
 * catalogue API returned; usage figures come only from the usage endpoint. They
 * are deliberately never joined: joining them would require rendering a
 * catalogue style with no usage rows as `0`, and the endpoint omits such a
 * style precisely because "no rows recorded" is not a measured zero.
 *
 * Below the original's grid sits the Quill-specific catalogue-health block:
 * active/withheld/missing-key counts, active-vs-cap per catalogue, and the
 * withheld list with each row's own `unavailable_reason`.
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Palette as StyleIcon,
  Create as WriteIcon,
  Image as IllustrationIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
} from 'recharts';

import { colors } from '../../../theme/designTokens';
import {
  CATALOGUE_BY_USAGE_CATEGORY,
  CHART_COLORS,
  MAX_ACTIVE_PER_CATALOGUE,
  USAGE_CATEGORIES,
  catalogueColor,
  usageCategoryLabel,
} from './constants';
import { useStyleUsage } from './useStyleUsage';
import { CatalogueKey, StyleFamily, StyleRow, StyleUsageItem, UsageCategory } from './types';

export interface CatalogueSummary {
  catalogue: CatalogueKey;
  label: string;
  family: StyleFamily;
  total: number;
  active: number;
  withheld: number;
  missingLsKey: number;
}

export interface AnalyticsPanelProps {
  summaries: CatalogueSummary[];
  /** Withheld rows across all catalogues, for the honest "can't be offered" list. */
  withheldRows: Array<{ key: string; label: string; row: StyleRow }>;
  /**
   * Display names for recorded keys, keyed `<usage category>:<style key>`.
   * Lookup only — a key that is not in here is rendered as the raw key and
   * labelled as such, never dropped and never renamed to a guess. A usage row
   * is a historical fact and outlives the catalogue row it names.
   */
  styleNames?: Record<string, string>;
}

/**
 * The recorded date, rendered plainly and deterministically.
 *
 * Explicit locale AND explicit UTC: `toLocaleDateString()` with neither is a
 * different string on a different machine, and this date is load-bearing — it
 * is the floor every usage number on this panel is counted from. The exact
 * instant rides along in the `title` attribute; the date itself is on screen,
 * never hidden behind a hover.
 */
function formatSince(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** The original's KPI tile: caption, h4 value, 40px icon at 0.7 opacity. */
const KpiCard: React.FC<{
  label: string;
  value: React.ReactNode;
  color: string;
  icon: React.ReactNode;
  /** Rendered under the value when the figure has no data source. */
  note?: string;
  testId: string;
}> = ({ label, value, color, icon, note, testId }) => (
  <Card sx={{ height: '100%' }} data-testid={testId}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" variant="body2">
            {label}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color }}>
            {value}
          </Typography>
          {note && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {note}
            </Typography>
          )}
        </Box>
        {icon}
      </Box>
    </CardContent>
  </Card>
);

/** A chart-sized honest empty state, so the card keeps its place and height. */
const EmptyChart: React.FC<{ message: string; detail: string }> = ({ message, detail }) => (
  <Box
    sx={{
      height: 300,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.5,
      border: '1px dashed #d1d5db',
      borderRadius: 2,
      px: 3,
      textAlign: 'center',
    }}
  >
    <Typography sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.9rem' }}>
      {message}
    </Typography>
    <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{detail}</Typography>
  </Box>
);

/**
 * The styles that were actually used, ranked, per catalogue.
 *
 * Renders ONLY what the endpoint returned. A style with no rows is absent from
 * the response and is absent here too — the closing note says so in as many
 * words, because a reader who does not know that would read this list as a
 * complete catalogue with the rest sitting at zero.
 */
const UsedStyles: React.FC<{
  categories: Partial<Record<UsageCategory, StyleUsageItem[]>>;
  days: number;
  sinceIso: string;
  styleNames: Record<string, string>;
}> = ({ categories, days, sinceIso, styleNames }) => (
  <Box data-testid="used-styles">
    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
      Composes counted since{' '}
      <Box component="span" sx={{ fontWeight: 700 }} title={sinceIso}>
        {formatSince(sinceIso)}
      </Box>
      . Each row shows the total since then, then the last {days} days.
    </Typography>

    <Box sx={{ height: 300, overflowY: 'auto', pr: 1 }}>
      {USAGE_CATEGORIES.map((category) => {
        const items = categories[category];
        const label = usageCategoryLabel(category);
        return (
          <Box key={category} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: catalogueColor(CATALOGUE_BY_USAGE_CATEGORY[category]),
                }}
              />
              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{label}</Typography>
            </Box>

            {items === undefined && (
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                Not included in this response.
              </Typography>
            )}
            {items && items.length === 0 && (
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                No uses recorded in this catalogue.
              </Typography>
            )}

            {(items ?? []).map((item) => {
              const name = styleNames[`${category}:${item.style_key}`];
              const lsNote =
                item.ls_style_keys.length === 0
                  ? 'sent with no LifeScribe key'
                  : item.ls_style_keys.join(', ') === item.style_key
                    ? null
                    : `LifeScribe received ${item.ls_style_keys.join(', ')}`;
              return (
                <Box
                  key={`${category}-${item.style_key}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 0.75,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      {name || item.style_key}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                      {name ? item.style_key : `${item.style_key} — not in the catalogue now`}
                      {lsNote ? ` · ${lsNote}` : ''}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {item.total}
                    </Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      · {item.window_count} in {days}d
                    </Box>
                  </Typography>
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>

    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 1 }}>
      Only styles with recorded rows appear. A style missing from this list has no rows, which is
      not the same as zero uses.
    </Typography>
  </Box>
);

const AnalyticsPanel: React.FC<AnalyticsPanelProps> = ({
  summaries,
  withheldRows,
  styleNames = {},
}) => {
  const theme = useTheme();
  const { usage } = useStyleUsage();

  const writingTotal = summaries
    .filter((s) => s.family === 'writing')
    .reduce((sum, s) => sum + s.total, 0);
  const illustrationTotal = summaries
    .filter((s) => s.family === 'illustration')
    .reduce((sum, s) => sum + s.total, 0);

  const total = summaries.reduce((sum, s) => sum + s.total, 0);
  const active = summaries.reduce((sum, s) => sum + s.active, 0);
  const withheld = summaries.reduce((sum, s) => sum + s.withheld, 0);
  const missingKeys = summaries.reduce((sum, s) => sum + s.missingLsKey, 0);

  /** Real per-catalogue counts. Keyed `name` so the slice labels actually render. */
  const stylesByCategory = summaries
    .filter((s) => s.total > 0)
    .map((s) => ({ name: s.label, count: s.total, catalogue: s.catalogue }));

  /**
   * The usage tile. A NUMBER APPEARS IN EXACTLY ONE STATE. Everywhere else the
   * tile keeps the em dash and says which fact is behind it, because "0" would
   * claim a measurement that was never taken.
   */
  const usageNote = (() => {
    switch (usage.kind) {
      case 'ready':
        return `Since ${formatSince(usage.data.since as string)} · ${usage.data.window_total} in the last ${usage.data.window.days} days`;
      case 'empty':
        return 'Not recorded yet — the usage table is in place and empty';
      case 'not-recording':
        return 'Not recording yet — migration 570 is not applied here';
      case 'error':
        return 'Usage request failed — unknown, not zero';
      default:
        return 'Loading…';
    }
  })();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Catalogue figures are counted from the rows the catalogue API returned. Usage figures count
        the styles that actually went on a chapter compose — one per style key per compose, so a
        chapter carrying both a writing style and an illustration style counts once in each. There
        is no backfill: every usage figure is counted from the date shown beside it and never from
        the beginning of time, and a style with no recorded rows is left out rather than shown as
        zero. A count is a use, not a choice — a chapter composed with the seeded default author
        counts, because it really was written in that voice. Quill collects no style ratings
        anywhere, so Avg Rating has no number to show.
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Writing Styles"
            testId="kpi-writing-styles"
            value={writingTotal}
            color={theme.palette.primary.main}
            icon={
              <WriteIcon sx={{ color: theme.palette.primary.main, opacity: 0.7, fontSize: 40 }} />
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Illustration Styles"
            testId="kpi-illustration-styles"
            value={illustrationTotal}
            color={theme.palette.secondary.main}
            icon={
              <IllustrationIcon
                sx={{ color: theme.palette.secondary.main, opacity: 0.7, fontSize: 40 }}
              />
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Style uses recorded"
            testId="kpi-style-uses"
            value={usage.kind === 'ready' ? usage.data.total : '—'}
            note={usageNote}
            color={theme.palette.success.main}
            icon={
              <TrendingUpIcon
                sx={{ color: theme.palette.success.main, opacity: 0.7, fontSize: 40 }}
              />
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Avg Rating"
            testId="kpi-avg-rating"
            value="—"
            note="No style ratings are collected"
            color={theme.palette.warning.main}
            icon={
              <StyleIcon sx={{ color: theme.palette.warning.main, opacity: 0.7, fontSize: 40 }} />
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Most used styles
              </Typography>
              {/*
                The original's "Usage Over Time" slot, at the same size, with
                what Quill can actually back. The usage endpoint returns totals
                per style and no time buckets, so a trend line here would be a
                shape nobody measured — which is exactly what the original drew
                with Math.random().
              */}
              {usage.kind === 'ready' ? (
                <UsedStyles
                  categories={usage.data.categories}
                  days={usage.data.window.days}
                  sinceIso={usage.data.since as string}
                  styleNames={styleNames}
                />
              ) : usage.kind === 'loading' ? (
                <EmptyChart
                  message="Loading usage…"
                  detail="Reading what members have composed with."
                />
              ) : usage.kind === 'not-recording' ? (
                <EmptyChart
                  message="Not recording yet"
                  detail={`${usage.message} Nothing is being counted here, which is not the same as nothing being used.`}
                />
              ) : usage.kind === 'empty' ? (
                <EmptyChart
                  message="Not recorded yet"
                  detail="Recording is on and no compose has been recorded yet. That is an absence of data, not zero uses — and there is no backfill, so nothing from before recording began will ever appear."
                />
              ) : (
                <EmptyChart
                  message="Usage could not be loaded"
                  detail={`${usage.message} This is a failed request, not an empty result — the figures are unknown right now, not zero.`}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Styles by Category
              </Typography>
              {total === 0 ? (
                <EmptyChart
                  message="No styles to chart yet"
                  detail="All four catalogues are empty."
                />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={stylesByCategory}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${((percent ?? 0) * 100).toFixed(0)}%`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {stylesByCategory.map((entry, i) => (
                        <Cell key={`cell-${entry.catalogue}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" gutterBottom>
        Catalogue health
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        What members can actually get, and what is being held back.
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Styles total"
            testId="kpi-styles-total"
            value={total}
            color={colors.deepTeal[500]}
            icon={<StyleIcon sx={{ color: colors.deepTeal[500], opacity: 0.7, fontSize: 40 }} />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Active (offered to members)"
            testId="kpi-active"
            value={active}
            color={colors.success.main}
            icon={
              <TrendingUpIcon sx={{ color: colors.success.main, opacity: 0.7, fontSize: 40 }} />
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Withheld"
            testId="kpi-withheld"
            value={withheld}
            color="#c2410c"
            icon={<WriteIcon sx={{ color: '#c2410c', opacity: 0.7, fontSize: 40 }} />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Missing LifeScribe key"
            testId="kpi-missing-ls-key"
            value={missingKeys}
            color={colors.error.main}
            icon={<IllustrationIcon sx={{ color: colors.error.main, opacity: 0.7, fontSize: 40 }} />}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active per catalogue
              </Typography>
              {summaries.map((s) => (
                <Box key={s.catalogue} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{s.label}</Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                      {s.active} active of {s.total} · cap {MAX_ACTIVE_PER_CATALOGUE}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (s.active / MAX_ACTIVE_PER_CATALOGUE) * 100)}
                    aria-label={`${s.label} active count`}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: colors.gray[200],
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: catalogueColor(s.catalogue),
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Withheld styles
              </Typography>
              {withheldRows.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Nothing is being withheld — every stored style is either offered or simply
                  inactive.
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    Stored in the catalogue but deliberately not offered. The reason is exactly what
                    the row carries.
                  </Typography>
                  {withheldRows.map((entry) => (
                    <Box
                      key={`${entry.label}-${entry.key}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: 1.2,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Chip
                        size="small"
                        label={entry.label}
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                      <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        {entry.row.display_name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                        — {entry.row.unavailable_reason}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsPanel;
